import { useEffect, useState } from "react";
import { Info } from "@phosphor-icons/react";
import api from "../../lib/api";

/**
 * Tiny one-line scope notice shown above the Schedule tab.
 *
 * Why this exists: truck owners with Google/Outlook/iCal sync reasonably
 * assume entries in the Schedule tab also push to their calendar. They
 * don't — only one-off "Today's post" pins do. Without this notice
 * owners hit confused-silent-fail (Jason caught it on Feb 2026). The
 * notice ONLY renders when the owner has a connected calendar — keeps
 * the editor uncluttered for owners who don't use calendar sync.
 */
export default function ScheduleCalendarSyncNotice() {
  const [hasAny, setHasAny] = useState(false);
  const [hidden, setHidden] = useState(
    () => typeof window !== "undefined" &&
      localStorage.getItem("schedule_sync_notice_dismissed") === "1",
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/calendar-sync-status");
        if (!cancelled) setHasAny(Boolean(data?.has_any));
      } catch {
        // status endpoint is best-effort — if it fails, we just don't
        // show the notice. Worst case: same UX as before this notice.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!hasAny || hidden) return null;

  return (
    <div
      data-testid="schedule-calendar-sync-notice"
      className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100"
    >
      <Info weight="duotone" className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
      <div className="flex-1">
        <span className="font-medium">Heads up:</span> entries in the weekly
        schedule are TruckTrackr-only — they don&apos;t push to your connected
        calendar. To put a stop in your Google / Outlook calendar too, use{" "}
        <span className="font-medium">Today&apos;s post → New post</span> instead.
      </div>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem("schedule_sync_notice_dismissed", "1");
          setHidden(true);
        }}
        className="text-amber-700 hover:text-amber-900 font-medium shrink-0 px-1"
        aria-label="Dismiss this notice"
        data-testid="schedule-calendar-sync-notice-dismiss"
      >
        Got it
      </button>
    </div>
  );
}
